import React, { useState, useImperativeHandle, useEffect } from 'react';
import { BarChart } from '@mui/icons-material';
import { Developer } from '../types/DeveloperTypes';
import MetricRow from './MetricRow';
import { formatMarkdown } from '../utils/markdownFormatter';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

interface DeveloperTrumpCardProps {
  developer: Developer;
  isTopPerformer?: boolean;
  aiSummary?: string;
  active?: boolean;
  onClick: () => void;
  onCloseActive?: () => void;
  isMobile?: boolean;
  onMoreInfo?: () => void;
  onMobileDataClick?: () => void;
  cardRef?: React.RefObject<{ flipToFront: () => boolean } | null>;
}

// CSS for card flip animation
const cardFlipStyles = `
  .trump-card-wrapper {
    
  }
  
  .trump-card-container {
    perspective: 1000px;
    width: 240px;
    height: 340px;
  }
  
  .trump-card-inner {
    position: relative;
    width: 100%;
    height: 100%;
    transition: transform 0.6s;
    transform-style: preserve-3d;    

  }
  
  .trump-card-inner.flipped {
    transform: rotateY(180deg);
  }
  
  .trump-card-front, .trump-card-back {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);    
    cursor: pointer;    
  }
  
  .trump-card-wrapper:hover .trump-card-front {
    box-shadow: 0 6px 12px rgba(0,0,0,0.15);
  }
  
  .trump-card-wrapper {
    transition: transform 0.2s ease-out;
  }
  
  .trump-card-back {
    transform: rotateY(180deg);
    display: flex;
    flex-direction: column;
  } 

  .trump-card-front-metrics {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 0px 12px 0px 12px;
  }

  .trump-card-header {
    margin: -12px -12px 10px -12px;
    padding: 14px 20px 8px 20px;
    color: white;
    font-weight: bold;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .trump-card-header-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-height: 40px;
  }
  
  .trump-card-header-title {
    margin: 0;
    font-size: 16px;
  }
  
  .trump-card-new-badge {
    background: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: bold;
    margin-top: 4px;
  }
  
  .trump-card-placeholder {
    height: 24px;
  }

  .trump-card-back-metrics {
    flex: 1;
    overflow: auto;
    font-size: 14px;
    line-height: 1.4;
    padding: 10px;
  }
  .trump-card-footer {
    text-align: center;
    font-size: 12px;
    margin-top: 8px;
    margin-bottom: 6px;
    color: #666;
  }

  .trump-card-front-overall-score {
    text-align: center;
    margin-bottom: 12px;
    font-size: 20px;
    font-weight: bold;
  } 

  .trump-card-front-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    gap: 8px;
    padding: 0px 12px 0px 12px;
  }
  
  .trump-card-mobile-buttons {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 16px;
    padding: 0px 12px;
  }
  
  .trump-card-mobile-button {
    padding: 8px 16px;
    border: none;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  
  .trump-card-mobile-button.more-info {
    background: #007bff;
    color: white;
  }
  
  .trump-card-mobile-button.close {
    background: #6c757d;
    color: white;
  }
  
  .trump-card-mobile-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }
  
  .trump-card-close-button {
    position: absolute;
    top: 3px;
    right: 4px;
    background: rgba(0,0,0,0.15);
    border: none;
    color: #fff;
    font-size: 24px;
    cursor: pointer;
    line-height: 1;
    padding: 4px 10px;
    border-radius: 50%;
    opacity: 0.85;
    transition: all 0.2s ease;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .trump-card-back-metrics {
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #2c3e50;    
    border-radius: 12px;
    overflow-wrap: break-word;
  }

  .trump-card-back-metrics h1,
  .trump-card-back-metrics h2,
  .trump-card-back-metrics h3 {
    margin: 0.5em 0 0.3em;
    font-weight: bold;
    color: #1a73e8;
  }

  .trump-card-back-metrics p {
    margin: 0.4em 0;
  }

  .trump-card-back-metrics ul {
    padding-left: 1.2em;
    margin: 0.5em 0;
  }

  .trump-card-back-metrics li {
    margin-bottom: 0.3em;
  }

  .trump-card-back-metrics strong {
    font-weight: bold;
    color: #111;
  }

  .trump-card-back-metrics em {
    font-style: italic;
    color: #555;
  }
`;

// Top Trumps Style Card Component
const DeveloperTrumpCard: React.FC<DeveloperTrumpCardProps> = ({
  developer,
  isTopPerformer = false,
  aiSummary,
  active = false,
  onClick,
  onCloseActive,
  isMobile = false,
  onMoreInfo,
  onMobileDataClick,
  cardRef
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const cardColor = isTopPerformer ? '#e8f5e9' : developer.IsBottomPerformer ? '#ffebee' : '#fff781';
  const borderColor = isTopPerformer ? '#4caf50' : developer.IsBottomPerformer ? '#f44336' : '#9e9e9e';
  const headerColor = isTopPerformer ? '#2e7d32' : developer.IsBottomPerformer ? '#c62828' : '#b89900';
  const textColor = isTopPerformer ? '#2e7d32' : developer.IsBottomPerformer ? '#c62828' : '#9e9e9e';
  
  // Expose the flipToFront method through the ref
  useImperativeHandle(cardRef, () => ({
    flipToFront: () => {
      if (isFlipped) {
        setIsFlipped(false);
        return true; // Return true if card was flipped
      }
      return false; // Return false if card was already facing front
    }
  }));
  // Calculate overall score (average of all trends)
  const overallScore = (
    developer.StoryPointsTrend +
    developer.CompletionRateTrend +
    developer.ProductivityTrend +
    developer.CommitsTrend -
    Math.abs(developer.BugFixRateTrend) // Inverse for bug fix rate
  ) / 5;

  // Handle card click to flip
  const handleCardClick = (e: React.MouseEvent) => {
    if (active) {
      setIsFlipped(!isFlipped); //disabled until i can think what to put on the back. maybe a picture if there is one?
    }
    // else: do nothing, require activation first
  };



  // Default AI summary if none is provided
  const aiSummaryFinal = aiSummary || `No exec insights available for ${developer.DeveloperName}. This developer's performance metrics show ${overallScore > 0 ? 'positive' : 'negative'} trends overall.`;

  return (
    <>
      <style>{cardFlipStyles}</style>
      <div className={`trump-card-wrapper ${active ? 'active' : ''}`} style={{
        transform: active ? 'none' : undefined,
        transition: 'transform 0.2s ease-out'
      }} onMouseEnter={(e) => {
        if (!active && !isMobile) {
          e.currentTarget.style.transformOrigin = 'bottom left';
          e.currentTarget.style.transform = 'scale(1.05)';
        }
      }} onMouseLeave={(e) => {
        if (!active && !isMobile) {
          e.currentTarget.style.transformOrigin = 'bottom left';
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}>
        <div className="trump-card-container" onClick={handleCardClick}>
          <div className={`trump-card-inner ${isFlipped ? 'flipped' : ''}`}>
            {/* Front of card */}
            <div className="trump-card-front"
              style={{
                background: cardColor,
                border: `2px solid ${borderColor}`
              }}
            >
              {/* Card Header */}
              <div className="trump-card-header" style={{background: headerColor}}>
                <div className="trump-card-header-content">
                  <h3 className="trump-card-header-title">{developer.DeveloperName}</h3>
                  {developer.IsNew ? (
                    <span className="trump-card-new-badge" style={{color: headerColor}}>NEW</span>
                  ) : (
                    <div className="trump-card-placeholder"></div>
                  )}
                </div>
                {!active && developer.ProductivityAverage ? (
                  <span className="trump-card-productivity" style={{color: 'white'}}>
                    {developer.ProductivityAverage?.toFixed(1)}%
                  </span>
                ) : active && onCloseActive ? (
                  <>
                    {isMobile && (
                      <button
                        className="trump-card-close-button"
                        style={{ right: '40px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMobileDataClick?.();
                        }}
                        aria-label="Open data view"
                        title="Open data view"
                      >
                        <BarChart sx={{ fontSize: 18 }} />
                      </button>
                    )}
                    <button 
                      className="trump-card-close-button"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        // Ensure card is flipped back to front
                        if (isFlipped) {
                          setIsFlipped(false);
                          // Small delay to allow flip animation before closing
                          setTimeout(() => {
                            onCloseActive();
                          }, 300);
                        } else {
                          onCloseActive();
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15)';
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.background = 'rgba(0,0,0,0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.opacity = '0.85';
                        e.currentTarget.style.background = 'rgba(0,0,0,0.15)';
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = 'scale(0.95)';
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15)';
                      }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </>
                ) : null}
              </div>

              {/* Overall Score */}
              <div className="trump-card-front-overall-score">
                <div>Trending Score</div>
                <div style={{ color: developer.IsTopPerformer ? '#2e7d32' : developer.IsBottomPerformer ? '#c62828' : '#ca7700' }}>
                  {developer.ProductivityAverage?.toFixed(1)}%
                </div>
              </div>

              {/* Metrics */}
              <div className="trump-card-front-metrics">
                <MetricRow
                  label="Story Points"
                  value={developer.StoryPointsTrend}
                  inverse={false}
                />
                <MetricRow
                  label="Completion Rate"
                  value={developer.CompletionRateTrend}
                  inverse={false}
                />
                <MetricRow
                  label="Bug Fix Rate"
                  value={developer.BugFixRateTrend}
                  inverse={true}
                />
                <MetricRow
                  label="Productivity"
                  value={developer.ProductivityTrend}
                  inverse={false}
                />
                <MetricRow
                  label="Commits"
                  value={developer.CommitsTrend}
                  inverse={false}
                />
              </div>


              <div className="trump-card-front-actions" style={{ justifyContent: 'center' }}>
                  <div style={{ 
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#666',
                    fontStyle: 'italic'
                  }}>

                  </div>
                </div>

              <div className="trump-card-footer">
                Click to see explanation
              </div>
            </div>

            {/* Back of card with statistics explanations */}
            <div className="trump-card-back" style={{
              background: cardColor,
              border: `2px solid ${borderColor}`
            }}>
              {/* Card Header */}
              <div className="trump-card-header" style={{background: headerColor}}>
                <div className="trump-card-header-content">
                  <h3 className="trump-card-header-title">{developer.DeveloperName}</h3>
                  {developer.IsNew ? (
                    <span className="trump-card-new-badge" style={{color: headerColor}}>NEW</span>
                  ) : (
                    <div className="trump-card-placeholder"></div>
                  )}
                </div>
                {active && onCloseActive && (
                  <>
                    {isMobile && (
                      <button
                        className="trump-card-close-button"
                        style={{ right: '40px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMobileDataClick?.();
                        }}
                        aria-label="Open data view"
                        title="Open data view"
                      >
                        <BarChart sx={{ fontSize: 18 }} />
                      </button>
                    )}
                    <button
                      className="trump-card-close-button"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        // Ensure card is flipped back to front
                        if (isFlipped) {
                          setIsFlipped(false);
                          // Small delay to allow flip animation before closing
                          setTimeout(() => {
                            onCloseActive();
                          }, 300);
                        } else {
                          onCloseActive();
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15)';
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.background = 'rgba(0,0,0,0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.opacity = '0.85';
                        e.currentTarget.style.background = 'rgba(0,0,0,0.15)';
                      }}
                      onMouseDown={(e) => {
                        e.currentTarget.style.transform = 'scale(0.95)';
                      }}
                      onMouseUp={(e) => {
                        e.currentTarget.style.transform = 'scale(1.15)';
                      }}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </>
                )}
                
              </div>
              {/* explanation of each metric */}
              <div className="trump-card-back-metrics">
                <div className="trump-card-back-metrics-content">
                  
                  {/* story points */}
                  <div className="trump-card-back-metrics-explanation">
                    <h3>Story Points</h3>
                    <p><strong>What it is:</strong> Story Points are a measure of the complexity and effort required to complete user stories. They help teams estimate work in a relative way rather than using absolute time estimates.</p>
                    <p><strong>Why it matters:</strong> Story points help teams understand a developer's capacity and velocity over time. They provide a more stable measure of productivity than time-based estimates, as they account for varying complexity levels across different types of work.</p>
                    <p><strong>How we calculate it:</strong> In our app, we calculate story points as the total number of points from completed work items in the current iteration, weighted by the developer's contribution percentage to each item. For example, if a developer contributed 60% to a 5-point story, they would receive 3 points.</p>
                  </div>

                  {/* completion rate */}
                  <div className="trump-card-back-metrics-explanation">
                    <h3>Completion Rate</h3>
                    <p><strong>What it is:</strong> Completion Rate measures how consistently a developer delivers their assigned work items within the planned timeframe. It's a direct indicator of reliability and predictability in delivery.</p>
                    <p><strong>Why it matters:</strong> High completion rates indicate reliable delivery and good estimation skills. This metric helps teams identify developers who consistently meet their commitments and maintain project momentum.</p>
                    <p><strong>How we calculate it:</strong> We calculate this as (Completed Work Items / Total Assigned Work Items) * 100. A work item is considered complete when it reaches the "Done" state in our tracking system, which requires all acceptance criteria to be met and the work to be reviewed and approved.</p>
                  </div>

                  {/* bug fix rate */}  
                  <div className="trump-card-back-metrics-explanation">
                    <h3>Bug Fix Rate</h3>
                    <p><strong>What it is:</strong> Bug Fix Rate measures how effectively a developer handles and resolves bugs in the codebase. It reflects both the quality of their work and their ability to maintain existing code.</p>
                    <p><strong>Why it matters:</strong> This metric is crucial for understanding code quality and maintenance capabilities. A high bug fix rate indicates strong debugging skills and a commitment to code quality, while also helping identify areas where preventive measures might be needed.</p>
                    <p><strong>How we calculate it:</strong> We calculate this as (Resolved Bugs / Total Assigned Bugs) * 100. We also consider the time taken to resolve each bug, with faster resolutions weighted more heavily. Critical bugs resolved within 24 hours receive a 1.5x multiplier, while bugs taking longer than 5 days receive a 0.5x multiplier.</p>
                  </div>

                  {/* productivity */}
                  <div className="trump-card-back-metrics-explanation">
                    <h3>Productivity</h3>
                    <p><strong>What it is:</strong> Productivity is a comprehensive measure of a developer's overall effectiveness and contribution to the team. It combines multiple aspects of development work into a single score.</p>
                    <p><strong>Why it matters:</strong> This metric helps identify well-rounded developers who excel across different aspects of software development. It's particularly useful for understanding how developers balance feature development with code quality and team collaboration.</p>
                    <p><strong>How we calculate it:</strong> We use a weighted composite score that combines:</p>
                    <ul>
                      <li>Story point completion (40%): Measures feature delivery</li>
                      <li>Bug fix rate (30%): Measures code quality and maintenance</li>
                      <li>Code review participation (20%): Measures team collaboration</li>
                      <li>Build success rate (10%): Measures code stability</li>
                    </ul>
                    <p>Each factor is normalized to a 0-100 scale before applying the weights.</p>
                  </div>

                  {/* commits */}
                  <div className="trump-card-back-metrics-explanation">
                    <h3>Commits</h3>
                    <p><strong>What it is:</strong> Commits represent the frequency and quality of code contributions to the codebase. They track how actively a developer is contributing to the project and the nature of those contributions.</p>
                    <p><strong>Why it matters:</strong> Regular, high-quality commits indicate consistent progress and good development practices. This metric helps identify developers who maintain steady progress and follow good version control practices.</p>
                    <p><strong>How we calculate it:</strong> We track commits through our source control integration, considering:</p>
                    <ul>
                      <li>Frequency: Number of commits in the current iteration</li>
                      <li>Size: Lines changed (weighted by impact)</li>
                      <li>Quality: Test pass rate (commits passing all tests get a 1.2x multiplier)</li>
                      <li>Message quality: Well-documented commits receive a 1.1x multiplier</li>
                    </ul>
                    <p>The final score is normalized against team averages to account for different project contexts.</p>
                  </div>
                  
                </div>
              </div>

              <div className="trump-card-footer">
                Click to flip back
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeveloperTrumpCard;
